/**
   * Game-side audible feedback node ("Beatbox Hero's Sonifier").
   *
   * The voice-core library ships a neutral, do-nothing feedback sink so that
   * `audio/graph.ts` has no dependency on any consumer's presentation code.
   * This class is the consumer-side implementation that actually produces
   * audible cues (hit chirps and combo stabs) in response to gameplay events.
   *
   * It exposes an `output` GainNode so callers can wire it into their audio
   * graph if desired, mirroring the shape of the voice-core `Sonifier`.
   */
export class Sonifier {
    constructor(ctx) {
        Object.defineProperty(this, "output", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "ctx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "muted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.ctx = ctx;
        this.output = ctx.createGain();
        this.output.gain.value = 1;
        try {
            this.output.connect(ctx.destination);
        }
        catch {
            /* destination may be unavailable in some contexts */
        }
    }
    setMuted(muted) {
        this.muted = muted;
    }
    /**
     * Short chirp for a successful hit. Precision (0..1) brightens the tone;
     * an optional spectral centroid nudges the base pitch upward.
     */
    hit(precision, centroid) {
        if (this.muted)
            return;
        const p = Math.max(0, Math.min(1, precision));
        const baseHz = 440 + (centroid ?? 0) * 0.25 + p * 220;
        this.blip(baseHz, 0.08, 0.12 + p * 0.18, "triangle");
    }
    /**
     * Rising stab that scales with the current combo count.
     */
    combo(combo) {
        if (this.muted)
            return;
        const step = Math.min(combo, 24);
        const hz = 330 * Math.pow(2, step / 24);
        this.blip(hz, 0.14, 0.22, "sawtooth");
    }
    blip(freq, durSec, peak, type) {
        let osc;
        let gain;
        try {
            osc = this.ctx.createOscillator();
            gain = this.ctx.createGain();
        }
        catch {
            return;
        }
        const now = this.ctx.currentTime;
        osc.type = type;
        osc.frequency.setValueAtTime(freq, now);
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(peak, now + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + durSec);
        osc.connect(gain);
        gain.connect(this.output);
        osc.start(now);
        osc.stop(now + durSec + 0.02);
        osc.onended = () => {
            try {
                osc.disconnect();
                gain.disconnect();
            }
            catch {
                /* already disconnected */
            }
        };
    }
    dispose() {
        try {
            this.output.disconnect();
        }
        catch {
            /* already disconnected */
        }
    }
}
